import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const tracks = [
  {
    number: '01',
    icon: '🎬',
    title: 'Filmmakers',
    accent: 'teal',
    accentColor: '#2ABFA3',
    steps: [
      'Submit via FilmFreeway',
      'Get notified of selection',
      'Film screens globally',
      'Live Q&A',
      'Engagement data + $12K prizes',
      'Build a community around your film',
    ],
    cta: 'Submit Now →',
    link: '/submit',
  },
  {
    number: '02',
    icon: '🎟',
    title: 'Audience',
    accent: 'orange',
    accentColor: '#E8721C',
    steps: [
      'Connect with fellow film lovers',
      'Join watch parties',
      'React in real time',
      'Vote Audience Choice',
      'Talk to filmmakers',
    ],
    cta: 'Get Tickets →',
    link: '/tickets',
  },
  {
    number: '03',
    icon: '🤝',
    title: 'Sponsors',
    accent: 'gold',
    accentColor: '#D4A843',
    steps: [
      'Enter at $500',
      'Put your brand where audiences are paying attention',
      'Film Pre-Rolls + group buying ads',
      'Real engagement data',
      'Co-branded announcement',
    ],
    cta: 'Become a Sponsor →',
    link: '/sponsors',
  },
  {
    number: '04',
    icon: '📣',
    title: 'Affiliates',
    accent: 'teal',
    accentColor: '#2ABFA3',
    steps: [
      'Earn up to 15% commission — rate grows as you sell more',
      'Your audience gets 10% off every ticket',
      'Share your unique referral link and promo code',
      'Track conversions and commissions in your dashboard',
      'Monthly payouts via Stripe Connect',
    ],
    cta: 'Join the Program →',
    link: '/affiliates',
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

export default function HowItWorks() {
  return (
    <section className="bg-dark2 w-full py-24 px-6">
      <div className="max-w-[1200px] mx-auto">
        {/* Eyebrow */}
        <motion.p
          className="font-mono text-teal uppercase mb-4"
          style={{ fontSize: '0.7rem', letterSpacing: '0.25em' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          How It Works
        </motion.p>

        {/* Title */}
        <motion.h2
          className="font-display text-white leading-none mb-8"
          style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Four ways to be part of it.
        </motion.h2>

        {/* Full paragraph */}
        <motion.p
          className="font-light max-w-[640px]"
          style={{ color: 'rgba(245,245,240,0.65)', lineHeight: 1.8 }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Synema isn't just a film festival — it's a communal experience. Screenings happen as
          synchronized global watch parties, where thousands of viewers across the world are watching
          the same film at the same moment, reacting together in real time. After each screening,
          filmmakers join live interactive Q&As with their global audience — not a recorded panel, a
          real conversation happening right now. Watch with your best friend from across the street or
          a stranger from across the world who loves the same kind of films you do. Beyond the
          screenings, Synema brings festival-goers into break-out sessions covering craft, industry,
          and storytelling — led by guest speakers from across the independent film world. It's the
          full festival experience, rebuilt for everywhere.
        </motion.p>

        {/* Cards grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {tracks.map((track) => (
            <motion.div
              key={track.number}
              className="relative bg-dark3 rounded-lg overflow-hidden"
              style={{
                border: '1px solid rgba(255,255,255,0.06)',
              }}
              variants={cardVariants}
              whileHover={{
                y: -2,
                borderColor: 'rgba(255,255,255,0.15)',
                transition: { duration: 0.2 },
              }}
            >
              {/* Gradient top bar */}
              <div
                className="h-[2px] w-full"
                style={{
                  background: `linear-gradient(90deg, ${track.accentColor}, transparent)`,
                }}
              />

              <div className="p-8 relative">
                {/* Number watermark */}
                <span
                  className="absolute top-4 right-6 font-display leading-none select-none pointer-events-none"
                  style={{
                    fontSize: '5rem',
                    color: 'rgba(255,255,255,0.04)',
                  }}
                >
                  {track.number}
                </span>

                {/* Icon */}
                <span className="text-4xl block mb-4">{track.icon}</span>

                {/* Title */}
                <h3
                  className="font-display leading-none mb-6"
                  style={{ fontSize: '1.8rem', color: track.accentColor }}
                >
                  {track.title}
                </h3>

                {/* Steps */}
                <ul className="space-y-3 mb-8">
                  {track.steps.map((step) => (
                    <li
                      key={step}
                      className="text-sm"
                      style={{ color: 'rgba(245,245,240,0.65)' }}
                    >
                      <span style={{ color: track.accentColor }} className="mr-2">
                        →
                      </span>
                      {step}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to={track.link}
                  className="inline-block font-medium text-sm underline underline-offset-4"
                  style={{ color: track.accentColor }}
                >
                  {track.cta}
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
